import { useLazyQuery } from '@apollo/client'

import { APIStatus } from '@flowwai-frontend/shared/api-common'
import { GetMessagesForThreadParams } from '@flowwai-frontend/bps-officer-web/graphql'
import {
   convertToErrorType,
   CustomError,
   getAPIStatusFromNetworkStatus
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { MessageModel } from '@flowwai-frontend/shared-web/ai-chat-support/data-access'

import {
   GetMessagesForThreadQuery as Query,
   GetMessagesForThreadDocument as QueryDocument,
   GetMessagesForThreadQueryVariables as QueryVariables
} from './query.generatedTypes'
import useAPIResponseHandler from './responseHandler'

export interface FailureArgsType {
   error?: Error | null
   data?: CustomError<Query['getMessagesForThread']>
}

interface TriggerAPIArgsType {
   reqObj: GetMessagesForThreadParams

   onFailure?: (args: FailureArgsType) => void
   onSuccess?: (messageModels: MessageModel[], totalCount: number) => void
}

interface ReturnType {
   apiError: Error | null
   apiStatus: APIStatus

   triggerAPI: (args: TriggerAPIArgsType) => void
}

const useGetMessagesForThread = (): ReturnType => {
   const [getMessagesForThread, { error, networkStatus, data }] = useLazyQuery<
      Query,
      QueryVariables
   >(QueryDocument)

   const { handleAPIResponse } = useAPIResponseHandler()

   const triggerAPI = (args: TriggerAPIArgsType): void => {
      const requestObj: QueryVariables = {
         params: args.reqObj
      }

      getMessagesForThread({
         variables: requestObj,
         fetchPolicy: 'network-only',

         onError(error) {
            args.onFailure?.({ error })
         },
         onCompleted(data) {
            handleAPIResponse({
               data: data,
               onHandleError: (
                  errorMessage: string,
                  data: Query['getMessagesForThread']
               ) =>
                  args.onFailure?.({
                     data: new CustomError(errorMessage, data)
                  }),
               onSuccess: args.onSuccess
            })
         }
      })
   }

   const apiError = convertToErrorType(error)
   const apiStatus = getAPIStatusFromNetworkStatus(networkStatus, data)

   return {
      apiError,
      apiStatus,

      triggerAPI
   }
}

export default useGetMessagesForThread
